import React, { useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import styled, { useTheme } from 'styled-components';
import Icon from 'components/Icon';
import Loading from 'components/Loading';
import { POSModal, POSButton } from 'components/POS';
import Spacer from 'components/Spacer';
import { usePrintManager } from 'hooks/usePrintManager';
import { usePrintReceipt } from 'hooks/usePrintReceipt';
import { usePrintTickets } from 'hooks/usePrintTickets';
import Images from 'images';
import { AppContext } from 'shared/AppContext';
import { useCart } from 'shared/CartContext';
import { getPrintSettings } from 'shared/utils/printUtils';
import { useCheckoutPos } from './gql/queries/useCheckoutPOS';
import MobileDeliveryModal from './MobileDeliveryModal';
import { PrintingModal } from './PrintingModal';

const BodyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-grow: 1;
  gap: 16px;
`;

const BalanceWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px 20px;
  border-radius: 10px;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(0, 0, 0, 0.03)'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.08)'
        : 'rgba(0, 0, 0, 0.08)'};
`;

const RowDiv = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
`;

const BalanceText = styled.div`
  font-size: 20px;
  font-weight: 600;
  font-family: 'Barlow Condensed', sans-serif;
  text-align: left;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.primary || '#ffffff'
      : '#000033'};
`;

const MoneyText = styled.span`
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 40px;
  font-weight: 700;
  line-height: 48px;
  text-align: right;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.primary || '#ffffff'
      : '#000033'};

  @media (max-width: 768px) {
    font-size: 32px;
    line-height: 40px;
  }
`;

const AmountInput = styled.input`
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 40px;
  font-weight: 700;
  line-height: 48px;
  text-align: right;
  max-width: 170px;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: ${(props) => props.theme.colors?.primary || '#FA4616'};

  &::placeholder {
    color: ${(props) => props.theme.colors?.primary || '#FA4616'};
    opacity: 0.5;
  }

  &:focus {
    outline: none;
  }

  @media (max-width: 768px) {
    font-size: 32px;
    line-height: 40px;
    max-width: 140px;
  }
`;

const CalculatorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 12px;
  border-radius: 10px;
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255, 255, 255, 0.03)' : '#ffffff'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.08)'
        : 'rgba(0, 0, 0, 0.08)'};
  gap: 8px;
`;

const NumberInput = styled.div`
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 28px;
  font-weight: 700;
  text-align: center;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.primary || '#ffffff'
      : '#000033'};
  padding: 12px 8px;
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(0, 0, 0, 0.03)'};
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.06)'};
  }

  &:active {
    transform: scale(0.98);
  }

  @media (max-width: 768px) {
    font-size: 24px;
    padding: 10px 6px;
  }
`;

const BillButton = styled.div`
  width: 60px;
  height: 60px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: all 0.2s ease;

  /* In dark mode, invert only the dark text while preserving green */
  ${(props) =>
    props.theme.name === 'dark' &&
    `
    svg path[fill="rgb(0, 0, 0)"],
    svg path[fill="#000000"] {
      fill: #ffffff !important;
    }
    svg path[fill="rgb(12, 12, 12)"],
    svg path[fill="#0c0c0c"] {
      fill: #ffffff !important;
    }
  `}

  &:hover {
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(0, 0, 0, 0.03)'};
  }

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: 768px) {
    width: 50px;
    height: 50px;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
  margin-top: auto;
`;

const ChangeDueWrapper = styled(BalanceWrapper)`
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(250, 70, 22, 0.1)'
      : 'rgba(250, 70, 22, 0.05)'};
  border-color: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(250, 70, 22, 0.2)'
      : 'rgba(250, 70, 22, 0.15)'};
`;

const CashPayment = ({ isVisible, setIsVisible }) => {
  const theme = useTheme();
  const inputRef = useRef(null);

  const { printReceipt } = usePrintReceipt();
  const { printTickets } = usePrintTickets();

  const {
    total,
    tickets,
    accountId,
    setPurchaseId,
    event,
    isPrinting,
    setIsPrinting,
    account: eventAccount,
    setConnectionStatus,
    setCurrentStatus,
  } = useCart();

  const IPAddress = localStorage.getItem('readerIp');

  const {
    state: { account },
  } = useContext(AppContext);

  const availableAccount = account || eventAccount;

  const {
    calculator,
    printSettingEnabled,
    printReceiptEnabled,
    printReceiptWithTickets,
  } = getPrintSettings({ event, availableAccount });

  const [amountEntered, setAmountEntered] = useState();
  const [loading, setLoading] = useState(false);
  const [openDeliveryModal, setOpenDeliveryModal] = useState(false);
  const [noteClicked, setNoteClicked] = useState(false);

  const onNumberClicked = (value) => {
    setAmountEntered((amountEntered) =>
      amountEntered ? `${amountEntered}${value}` : value
    );
  };

  const onNoteClicked = (value) => {
    setAmountEntered((amountEntered) =>
      amountEntered ? amountEntered + value : value
    );
    setNoteClicked(true);
  };

  const deleteLast = () => {
    if (noteClicked) {
      setAmountEntered('');
      setNoteClicked(false);
      return;
    }
    setAmountEntered((amountEntered) => {
      if (amountEntered === 0) return 0.0;

      return amountEntered.length > 1
        ? amountEntered?.slice(0, -1) === ''
          ? ''
          : amountEntered.slice(0, -1)
        : '';
    });
  };

  const onChange = (e) => {
    setAmountEntered(e.target.value);
  };

  useEffect(() => {
    if (isVisible && inputRef.current) inputRef.current.focus();
  }, [isVisible]);

  useEffect(() => {
    if (isVisible && !calculator) handleConfirm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, calculator]);

  const params = useParams();
  const eventId = parseInt(params.id);

  const checkoutPos = useCheckoutPos();

  const printConfigOn =
    printSettingEnabled || printReceiptEnabled || printReceiptWithTickets;

  const printTicketAndReceipt = async (transaction) => {
    setCurrentStatus('Printing...');
    if (printSettingEnabled) {
      await print(transaction);

      if (printReceiptWithTickets) await printRec(transaction);
    }

    if (printReceiptEnabled) {
      await printRec(transaction);
    }

    setIsPrinting(false);
  };

  const handleConfirm = async () => {
    setLoading(true);
    const seatsio = JSON.parse(sessionStorage.getItem('seatsio'));
    const holdToken = seatsio?.holdToken;

    const allTickets = Object.values(tickets).reduce((acc, value) => {
      const tickets = value.tickets?.map((ticket) => ({
        id: ticket.id,
        qty: ticket.count,
        ...(ticket.reserved_seat && ticket.count > 0
          ? {
              seats: ticket.seats?.map((seat) => ({
                seat_id: seat.id,
                ...(seat?.type ? { type: seat.type } : null),
              })),
            }
          : null),
      }));

      return [...acc, ...tickets];
    }, []);

    const result = await checkoutPos({
      event_id: eventId,
      account_id: accountId,
      tickets: allTickets,
      hold_token: holdToken,
      payment_method: 'cash',
    });

    if (result && result?.data) {
      setAmountEntered('');
      setLoading(false);
      setIsVisible(false);
      setPurchaseId(result.data.checkoutPOS);

      if (printConfigOn && IPAddress) {
        setIsPrinting(true);
        const connectedPrinter = isConnected();
        if (!connectedPrinter && IPAddress) {
          setCurrentStatus('Connecting to printer...');
          try {
            const connected = await connect(
              IPAddress,
              setConnectionStatus,
              setIsVisible
            );
            if (connected) {
              printTicketAndReceipt(result?.data?.checkoutPOS);
            } else {
              console.error('Failed to connect to printer in cashPayment');
            }
          } catch (error) {
            console.error('Failed to connect to printer:', error);
          }
        } else if (connectedPrinter) {
          printTicketAndReceipt(result?.data?.checkoutPOS);
        }
      }

      setOpenDeliveryModal(true);
    }
  };

  const changeDue = amountEntered
    ? (+amountEntered - total.toString().replace('$', '') ?? 0).toFixed(2)
    : `-${total.toString().replace('$', '') ?? 0}`;

  const { connect, isConnected } = usePrintManager();

  const print = async (transaction) => {
    try {
      await printTickets({ transaction, setIsPrinting });
    } catch (e) {
      console.error(e);
    }
  };

  const printRec = async (transaction) => {
    try {
      await printReceipt({ transaction, setIsPrinting });
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <>
      <POSModal
        isVisible={isVisible}
        onClose={() => setIsVisible(false)}
        title="Cash Payment"
      >
        {loading || !calculator ? (
          <div
            style={{
              display: 'flex',
              flex: 1,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Loading />
          </div>
        ) : (
          <BodyWrapper>
            {/* Balance Due */}
            <BalanceWrapper>
              <RowDiv>
                <BalanceText>Balance Due</BalanceText>
                <MoneyText>{total}</MoneyText>
              </RowDiv>

              <Spacer size={12} />

              <RowDiv>
                <BalanceText>Cash Tendered</BalanceText>
                <AmountInput
                  id="amountEntered"
                  name="amountEntered"
                  onChange={onChange}
                  value={amountEntered}
                  ref={inputRef}
                  placeholder="0"
                />
              </RowDiv>
            </BalanceWrapper>

            {/* Calculator */}
            <CalculatorWrapper>
              <RowDiv>
                <BillButton onClick={() => onNoteClicked(10)}>
                  <Icon icon="10-Icon" size={60} />
                </BillButton>
                <NumberInput onClick={() => onNumberClicked(1)}>1</NumberInput>
                <NumberInput onClick={() => onNumberClicked(2)}>2</NumberInput>
                <NumberInput onClick={() => onNumberClicked(3)}>3</NumberInput>
              </RowDiv>
              <RowDiv>
                <BillButton onClick={() => onNoteClicked(20)}>
                  <Icon icon="20-Icon" size={60} />
                </BillButton>
                <NumberInput onClick={() => onNumberClicked(4)}>4</NumberInput>
                <NumberInput onClick={() => onNumberClicked(5)}>5</NumberInput>
                <NumberInput onClick={() => onNumberClicked(6)}>6</NumberInput>
              </RowDiv>
              <RowDiv>
                <BillButton onClick={() => onNoteClicked(50)}>
                  <Icon icon="50-Icon" size={60} />
                </BillButton>
                <NumberInput onClick={() => onNumberClicked(7)}>7</NumberInput>
                <NumberInput onClick={() => onNumberClicked(8)}>8</NumberInput>
                <NumberInput onClick={() => onNumberClicked(9)}>9</NumberInput>
              </RowDiv>
              <RowDiv>
                <BillButton onClick={() => onNoteClicked(100)}>
                  <Icon icon="100-Icon" size={60} />
                </BillButton>
                <NumberInput onClick={() => onNumberClicked('.')}>
                  .
                </NumberInput>
                <NumberInput onClick={() => onNumberClicked(0)}>0</NumberInput>
                <NumberInput onClick={deleteLast}>
                  <img
                    src={Images.delete}
                    width={24}
                    height={24}
                    alt="Delete"
                    style={{
                      filter:
                        theme?.name === 'dark'
                          ? 'brightness(0) invert(1)'
                          : 'none',
                    }}
                  />
                </NumberInput>
              </RowDiv>
            </CalculatorWrapper>

            {/* Change Due */}
            <ChangeDueWrapper>
              <RowDiv>
                <BalanceText>Change Due</BalanceText>
                <MoneyText
                  style={{ color: theme.colors?.primary || '#FA4616' }}
                >
                  ${changeDue}
                </MoneyText>
              </RowDiv>
            </ChangeDueWrapper>

            {/* Action Buttons */}
            <ButtonWrapper>
              <POSButton
                variant="outlined"
                style={{ flex: 1, padding: '14px 20px', fontSize: '18px' }}
                onClick={() => setIsVisible(false)}
              >
                Cancel
              </POSButton>

              <POSButton
                variant="primary"
                style={{ flex: 1, padding: '14px 20px', fontSize: '18px' }}
                onClick={handleConfirm}
                disabled={+changeDue < 0}
              >
                Confirm
              </POSButton>
            </ButtonWrapper>
          </BodyWrapper>
        )}
      </POSModal>

      <MobileDeliveryModal
        isVisible={openDeliveryModal}
        setIsVisible={setOpenDeliveryModal}
        showSuccessMessage={true}
      />

      <PrintingModal
        isVisible={isPrinting}
        printSettingEnabled={printSettingEnabled}
        printReceiptEnabled={printReceiptEnabled}
        printReceiptWithTickets={printReceiptWithTickets}
        setOpenSuccessModal={setOpenDeliveryModal}
      />
    </>
  );
};

export default CashPayment;
